import {Box, Divider, SelectChangeEvent} from '@mui/material';
import {Dayjs} from 'dayjs';
import {t} from 'i18next';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  getDateAndTimeOperators,
  getIsInOperators,
  getMonthOperators,
  QBOperators,
} from '../../../../utils/queryBuilder';
import AurDatePicker from '../../../common/aur-date-picker/aur-date-picker';
import AurTimePicker from '../../../common/aur-time-picker/AurTimePicker';
import {
  FieldUIType,
  FieldUITypeMappingObj,
  IFieldModal,
} from '../../field-management/custom-field/constant';
import {QueryFieldDropdown} from './components/QueryFieldDropdown';

export interface IQueryFieldDateTimeParams {
  operator?: QBOperators;
  genericValue?: string;
  startDate?: Dayjs;
  startTime?: Dayjs;
  endDate?: Dayjs;
  endTime?: Dayjs;
  periodType?: QBOperators;
  periodValue?: QBOperators;
}

interface QueryFieldDateTimeProps {
  selectedField?: IFieldModal;
  initialInputValue?: IQueryFieldDateTimeParams;
  onChangeFieldParams?: (params: IQueryFieldDateTimeParams) => void;
}

const QueryFieldDateTime: React.FC<QueryFieldDateTimeProps> = ({
  selectedField,
  initialInputValue,
  onChangeFieldParams,
}) => {
  const [selectedOperator, setSelectedOperator] = useState<
    IQueryFieldDateTimeParams['operator']
  >(getDateAndTimeOperators()[0].name);
  const [startDate, setStartDate] = useState<Dayjs>();
  const [startTime, setStartTime] = useState<Dayjs>();
  const [endDate, setEndDate] = useState<Dayjs>();
  const [endTime, setEndTime] = useState<Dayjs>();
  const [periodType, setPeriodType] =
    useState<IQueryFieldDateTimeParams['operator']>();
  const [periodValue, setPeriodValue] =
    useState<IQueryFieldDateTimeParams['operator']>();

  const [isStartPicker, setIsStartPicker] = useState<boolean>(false);
  const [isEndPicker, setIsEndPicker] = useState<boolean>(false);

  useEffect(() => {
    if (initialInputValue) {
      setSelectedOperator(initialInputValue.operator);
      setStartDate(initialInputValue?.startDate ?? undefined);
      setStartTime(initialInputValue?.startTime ?? undefined);
      setEndDate(initialInputValue?.endDate ?? undefined);
      setEndTime(initialInputValue?.endTime ?? undefined);
      setPeriodType(initialInputValue.periodType);
      setPeriodValue(initialInputValue.periodValue);
    }
  }, [initialInputValue]);

  const fieldUIType =
    FieldUITypeMappingObj[selectedField?.fieldTypeId as FieldUIType];

  const isTimeOnly = fieldUIType === 'Time';

  const isDateOnly = fieldUIType === 'Date';

  const isDateTime = fieldUIType === 'DateTime';

  const resetField = () => {
    setStartDate(undefined);
    setStartTime(undefined);
    setEndDate(undefined);
    setEndTime(undefined);
    setPeriodType(undefined);
    setPeriodValue(undefined);
  };

  const handleConditionChange = useCallback(
    (event: SelectChangeEvent) => {
      const name = getDateAndTimeOperators().find(
        (op) => op.label === event.target.value
      )?.name;
      setSelectedOperator(name);
      setIsStartPicker(false);
      setIsEndPicker(false);
      onChangeFieldParams?.({
        operator: name,
      });
      resetField();
    },
    [onChangeFieldParams]
  );

  const handlePeriodType = useCallback(
    (event: SelectChangeEvent) => {
      const name = getIsInOperators().find(
        (op) => op.label === event.target.value
      )?.name;
      setPeriodType(name);
      setPeriodValue(undefined);
      onChangeFieldParams?.({
        operator: selectedOperator,
        periodType: name,
      });
    },
    [onChangeFieldParams, selectedOperator]
  );

  const handlePeriodValue = useCallback(
    (event: SelectChangeEvent) => {
      const name = getMonthOperators().find(
        (op) => op.label === event.target.value
      )?.name;
      setPeriodValue(name);
      onChangeFieldParams?.({
        operator: selectedOperator,
        periodType: periodType,
        periodValue: name,
      });
    },
    [onChangeFieldParams, periodType, selectedOperator]
  );

  const disableInput = useMemo(
    () =>
      selectedOperator === QBOperators.isSet ||
      selectedOperator === QBOperators.isNotSet ||
      selectedOperator === QBOperators.isNotSetText ||
      selectedOperator === QBOperators.isSetText,
    [selectedOperator]
  );

  useEffect(() => {
    if (disableInput) {
      resetField();
      onChangeFieldParams?.({
        operator: selectedOperator,
        genericValue: '*',
      });
    }
  }, [disableInput, onChangeFieldParams, selectedOperator]);

  const renderIsIn = () => {
    return (
      <>
        <QueryFieldDropdown
          dateTestId="duration-range"
          selectedOperator={periodType}
          operators={getIsInOperators()}
          handleConditionChange={handlePeriodType}
        />
        {periodType === 'Month' && (
          <QueryFieldDropdown
            dateTestId="month"
            selectedOperator={periodValue}
            operators={getMonthOperators()}
            handleConditionChange={handlePeriodValue}
          />
        )}
      </>
    );
  };

  const renderDateTimeView = () => {
    return (
      <Box className="date-time-input-container">
        {(isDateOnly || isDateTime) && (
          <Box className="date-input-container">
            <AurDatePicker
              enableDateInputForBetween
              placeholder={t('indexSearchPage.startDate')}
              advanceLink={false}
              buttonProps={{
                startIcon: undefined,
                className: isStartPicker
                  ? 'aur-date-picker-button aur-date-picker-button-selected'
                  : 'aur-date-picker-button',
                disabled: disableInput,
              }}
              clearDates={selectedOperator === 'Between'}
              popperProps={{open: isStartPicker}}
              selectedDate={startDate && [startDate]}
              startCalendarProps={{maxDate: endDate}}
              onApplyClick={(values) => {
                setStartDate(values[0]);
                onChangeFieldParams?.({
                  operator: selectedOperator,
                  startDate: values[0],
                  endDate,
                  startTime,
                  endTime,
                });
              }}
              onPopup={(type) =>
                type === 'open'
                  ? setIsStartPicker(true)
                  : setIsStartPicker(false)
              }
            />
            <Divider
              aria-hidden="true"
              orientation="horizontal"
              flexItem
              className="aur-divider"
            />
            <AurDatePicker
              enableDateInputForBetween={selectedOperator === 'Between'}
              placeholder={t('indexSearchPage.endDate')}
              advanceLink={false}
              buttonProps={{
                startIcon: undefined,
                className: isEndPicker
                  ? 'aur-date-picker-button aur-date-picker-button-selected'
                  : 'aur-date-picker-button',
                disabled: selectedOperator !== 'Between' || disableInput,
              }}
              clearDates={selectedOperator === 'Between'}
              popperProps={{open: isEndPicker}}
              selectedDate={endDate && [endDate]}
              startCalendarProps={{minDate: startDate}}
              onApplyClick={(values) => {
                setEndDate(values[0]);
                onChangeFieldParams?.({
                  operator: selectedOperator,
                  startDate,
                  endDate: values[0],
                  startTime,
                  endTime,
                });
              }}
              onPopup={(type) =>
                type === 'open' ? setIsEndPicker(true) : setIsEndPicker(false)
              }
            />
          </Box>
        )}
        {(isTimeOnly || isDateTime) && (
          <Box className="date-input-container">
            <AurTimePicker
              placeholder={t('indexSearchPage.startTime')}
              value={startTime}
              disabled={disableInput}
              onAccept={(time) => {
                setStartTime(time);
                onChangeFieldParams?.({
                  operator: selectedOperator,
                  startDate,
                  endDate,
                  startTime: time,
                  endTime,
                });
              }}
            />
            <Divider
              aria-hidden="true"
              orientation="horizontal"
              flexItem
              className="aur-divider"
            />
            <AurTimePicker
              placeholder={t('indexSearchPage.endTime')}
              value={endTime}
              disabled={selectedOperator !== 'Between' || disableInput}
              onAccept={(time) => {
                setEndTime(time);
                onChangeFieldParams?.({
                  operator: selectedOperator,
                  startDate,
                  endDate,
                  startTime,
                  endTime: time,
                });
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderFormBaseOnOperator = () => {
    return selectedOperator === 'Is in' ? renderIsIn() : renderDateTimeView();
  };

  return (
    <Box className={'datetime-field-parent-container'}>
      <QueryFieldDropdown
        fieldLabel={selectedField?.fieldLabel}
        selectedOperator={selectedOperator}
        operators={getDateAndTimeOperators()}
        handleConditionChange={handleConditionChange}
      />
      {renderFormBaseOnOperator()}
    </Box>
  );
};

export default QueryFieldDateTime;
